"use client";

import { HeroSection } from "@/components/shared/hero_section";
import { BenefitsSection } from "@/components/shared/benefits_section";
import { EvidenceSection } from "@/components/shared/evidence_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { CloudflareStreamVideo } from "@/components/shared/CloudflareStreamVideo";
import { QuoteCarousel, QuoteData } from "@/components/shared/quote_carousel";
import { ImagesRow } from "@/components/shared/images_row";
import { useParams } from "next/navigation";
import { get__video_for_locale } from "@/constants/videos";

const Students = () => {
    const params = useParams();
    const locale = params?.locale as string || 'en-gb';
    // Student testimonial quotes
    const studentQuotes: QuoteData[] = [
        {
            text: "Just thought I'd let you know that the app works great, I'm really glad its been made possible. I'll be sure to leave a review on the app store for it!",
            author: "Ryan Peddie",
            title: "BA Policy, Politics and Economics",
            image: "/ryan_peddie.jpeg",
            logo: "/uob_crest.png"
        },
        {
            text: "You also have the test feature. It helps you beat procrastination because it's a lot easier to just sit down and do a practice test, you know, like get actually involved for 40 minutes rather than dreading it all year. It's a good way to start revising.",
            author: "Abdubasit Agberemi",
            title: "BSc Sport, Exercise and Health Sciences",
            image: "/Abdubasit-Agberemi.png",
            logo: "/uob_crest.png"
        },
        {
            text: "I have extra time because of my dyslexia and I like Studystash because I use the flashcards all the time on my Education with Mandarin degree. I get bored easily so sometimes I learn in Mandarin which makes it more fun. This is the first time it felt like things started to click",
            author: "Aaisha Rutuja",
            title: "Coventry University",
            image: "/aaisha-rutuja.png",
            logo: "/coventry_uni.png"
        },
        {
            text: "The platform was a big help when I fell behind on lectures for my optometry degree at Cardiff. It made catching up much quicker and getting organised before exams so much easier. Exactly what I needed to stay on track.",
            author: "Peter Taylor",
            title: "Optometry with Preliminary Year (MOptom)",
            image: "/case-studies/peter_taylor.png",
            logo: "/case-studies/cardiff.png"
        },
        {
            text: "StudyStash gave me detailed revision notes for three days of lectures. It was really helpful and definitely useful for condensed knowledge... it's kind of like a pill-sized pieces of information to grasp concepts without going into too in-depth",
            author: "Dominika Gorgosz",
            title: "MSc Health Data Science",
            image: "/case-studies/dominika.png",
            logo: "/uob_crest.png"
        },
        {
            text: "I found StudyStash incredibly useful, especially for generating flashcards from my lecture notes. It's been a huge help in organising my revision and preparing for exams.",
            author: "Tedi Georgieva",
            title: "BSc Medical Biochemistry",
            image: "/case-studies/tedi_georgieva.png",
            logo: "/uob_crest.png"
        },
        {
            text: "StudyStash provides a space to compile and organise all my study materials, making it so much easier to review all my topics. Revision doesn't seem like a chore anymore...",
            author: "Maxence Fix",
            title: "BEng Chemical Engineering",
            image: "/case-studies/maxence_fix.png",
            logo: "/uob_crest.png"
        },
    ];

    return (
        <main className="pt-8">
            <div className="flex w-full flex-col items-center">
                <HeroSection
                    desktopImage="/apple_images.png"
                    mobileImage="/apple_image_mobile.png"
                    desktopImageAlt="StudyStash Dashboard shown on a MacBook Pro, iPad and iPhone"
                    mobileImageAlt="StudyStash Dashboard shown on a MacBook Pro"
                    heading="Accurate [flashcards] from your course material in seconds"
                    animatedWordsStyle="lg"
                    showDemoButton={false}
                    animatedWords={[
                        {
                            words: ["flashcards", "tests and quizzes", "explainers", "revision sessions", "mindmaps", 'virtual tutors'],
                            interval: 1700,
                            colors: ["bg-ss-saffron", "bg-ss-green", "bg-ss-purple", "bg-ss-yellow", "bg-ss-coral", "bg-ss-azure"]
                        }
                    ]}
                />

                <div className="pt-10 sm:pt-12 md:pt-24 lg:pt-64"></div>

                <div id="video" className="relative mx-auto w-full max-w-4xl px-4 sm:px-6 py-12 sm:py-16 md:py-20">
                    <div className="w-full overflow-x-auto rounded-xl shadow-lg ring-1 ring-black/5 backdrop-blur-sm">
                        <CloudflareStreamVideo
                            uid={get__video_for_locale(locale, "STUDENT_EXPLAINER_VIDEOS")}
                            poster={"https://studystash.com/placeholder_thumbnail_video.png"}
                        />
                    </div>
                </div>

                <div className="relative w-full">
                    <div className="absolute top-[400px] right-0 pointer-events-none hidden lg:block">
                        <img
                            src="/left-right-lines.svg"
                            alt="Illustration of the StudyStash teaching-learning loop"
                            className="w-[300px] h-auto"
                        />
                    </div>

                    <BenefitsSection
                        heading="Learning is hard"
                        subheading1="StudyStash's AI study tools automatically track your progress and provide you insights into your strengths and weaknesses."
                        subheading2={`The tools then adapt to your learning style and help you create study materials that boost your understanding, grades and exam confidence!`}
                        cards={[
                            {
                                title: "Flashcards, notes and more",
                                description: "Upload lecture notes and StudyStash creates revision assets, all from your lecturers content.",
                                image: "/for-students-flashcards-notes.png",
                                imageAlt: "Lecture notes illustration",
                                bgColor: "bg-ss-blue"
                            },
                            {
                                title: "Study together",
                                description: "Invite your friends and create course notes together and compete to stay at the top of your groups leaderboard!",
                                image: "/badges.png",
                                imageAlt: "Flashcards illustration",
                                bgColor: "bg-ss-purple"
                            },
                            {
                                title: "StudyChat AI",
                                description: "Revise and learn in 150+ languages with your virtual tutor, StudyChat, to understand complex concepts.",
                                image: "/studychat.png",
                                imageAlt: "Flashcards illustration",
                                bgColor: "bg-ss-green"
                            },
                        ]}
                        buttonText="See how it works"
                        showButton={false}
                    />
                </div>

                <div className="pt-12 md:pt-32 lg:pt-64 w-full">
                    <EvidenceSection
                        heading="Students using StudyStash achieve more in less time"
                        source="Survey of 500 StudyStash students (01/10/2024 – 01/10/2025)"
                        description="StudyStash helps students improve their grades, study more efficiently, and approach exams with greater confidence."
                        circles={[
                            {
                                value: "68%",
                                title: "Grade improvement",
                                description: "Of students reported higher grades after using StudyStash"
                            },
                            {
                                value: "30%",
                                title: "Time saved",
                                description: "Average reduction in study time reported by students"
                            },
                            {
                                value: "87%",
                                title: "Confidence boost",
                                description: "Of students felt more confident going into exams"
                            }
                        ]}
                    />
                </div>

                <div className="w-full pt-20 sm:pt-32 pb-4 sm:pb-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6">How We Compare</h2>
                            <p className="text-lg mb-12">Unlike other platforms, StudyStash helps you truly understand, retain, and apply your course content through our innovative AI-powered learning tools.</p>
                            <div className="flex justify-center pt-10">
                                <img src="/other_tools.png" alt="StudyStash comparison with other tools" className="max-w-full h-auto" />
                            </div>
                        </div>
                    </div>
                </div>

                <ImagesRow
                    heading="Get insights. Practice what matters. Track your progress."
                    description="StudyStash automatically tracks your progress, providing insights into your strengths and weaknesses and creating study tools that match them."
                    cards={[
                        {
                            image: "/flashcard.png",
                            imageAlt: "Lecture notes illustration",
                            bgColor: "bg-ss-blue"
                        },
                        {
                            image: "/badges_sharing.png",
                            imageAlt: "Flashcards illustration",
                            bgColor: "bg-ss-purple"
                        },
                        {
                            image: "/studychat_languages.png",
                            imageAlt: "Flashcards illustration",
                            bgColor: "bg-ss-green"
                        }
                    ]}
                />

                <div className="relative w-full py-24">
                    {/* Background SVG for LeftRightSection */}
                    <div className="absolute left-0 top-[300px] w-full h-1/2 pointer-events-none z-0">
                        <img
                            src="/left-right-loops.svg"
                            alt="Illustration of the StudyStash teaching-learning loop"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    <div className="relative z-10">
                        <AlternatingSection
                            sections={[
                                {
                                    subtitle: "Exam preparation",
                                    title: "Create practice tests",
                                    content: (
                                        <div>
                                            <p>All from your course material, specifically focused on areas you're struggling with. Students typically see improvements in their results within just 2 weeks of starting with StudyStash!</p>
                                        </div>
                                    ),
                                    video: {
                                        src: "/walkthrough/tests/generate_test.mp4",
                                        alt: "Demonstration of test generation feature",
                                        containerClassName: "rounded-xl bg-ss-blue/20 /px-3 /pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8",
                                        type: "mp4"
                                    }
                                },
                                {
                                    subtitle: "10+ learning modes",
                                    title: "StudyMatch games",
                                    content: (
                                        <div>
                                            <p>Convert your teacher’s content into quick interactive exercises to test and reinforce your knowledge!</p>
                                        </div>
                                    ),
                                    video: {
                                        src: "/walkthrough/flashcards/matching_mode.mp4",
                                        alt: "Demonstration of matching game mode",
                                        containerClassName: "rounded-xl bg-ss-yellow/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8",
                                        type: "mp4"
                                    }
                                },
                            ]}
                        />
                    </div>
                </div>

                <div className="pb-24 w-full container mx-auto px-6 lg:px-8">
                    <QuoteCarousel quotes={studentQuotes} />
                </div>
            </div>
        </main>
    );
};

export default Students;
